import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, fmtDateTime } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Panel, inputCls, btnPrimary } from "./ui";
import { BrandLoader } from "../components/site/Logo";

const ROLE_CODES = ["SUPER_ADMIN", "ADMIN", "EDITOR_IN_CHIEF", "EDITOR", "JOURNALIST", "FACT_CHECKER", "MARKETING", "SALES", "FINANCE", "PARTNER"];

const InviteForm = ({ onDone }) => {
  const [f, setF] = useState({ email: "", role_code: "JOURNALIST" });
  const [link, setLink] = useState(null);
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/invitations", f);
      toast.success(data.email_sent ? "Pozivnica poslana emailom" : "Pozivnica kreirana (email nije poslan)");
      setLink(data);
      setF({ email: "", role_code: "JOURNALIST" });
      onDone();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Panel title="Pozovi člana redakcije" className="mb-6" testId="invite-panel">
      <form onSubmit={submit} className="grid gap-3 md:grid-cols-[1fr_220px_auto]">
        <input type="email" required placeholder="email@domena.de" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className={inputCls} data-testid="invite-email-input" />
        <select value={f.role_code} onChange={(e) => setF({ ...f, role_code: e.target.value })} className={inputCls} data-testid="invite-role-select">{ROLE_CODES.map((r) => <option key={r}>{r}</option>)}</select>
        <button className={btnPrimary} data-testid="invite-submit">Pošalji pozivnicu</button>
      </form>
      {link && (
        <div className="mt-3 rounded-md bg-muted p-3 text-xs" data-testid="invite-result">
          <p>Email: {link.email_sent ? "poslan" : `nije poslan (${link.email_info})`}. Link (vrijedi 7 dana, jednokratan) možete i sami proslijediti:</p>
          <code className="mt-1 block break-all" data-testid="invite-link">{link.invite_link}</code>
        </div>
      )}
      <p className="mt-2 text-xs text-muted-foreground">Registracija je moguća isključivo putem pozivnice.</p>
    </Panel>
  );
};

const Activity = ({ u, canRevoke }) => {
  const [d, setD] = useState(null);
  const load = () => api.get(`/admin/users/${u.id}/activity`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const revoke = async () => {
    if (!window.confirm(`Odjaviti ${u.name} sa svih uređaja?`)) return;
    try { const { data } = await api.post(`/admin/users/${u.id}/sessions/revoke`); toast.success(`Prekinuto sesija: ${data.revoked}`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (!d) return <p className="text-xs">Učitavanje…</p>;
  const active = d.sessions.filter((s) => s.active);
  return (
    <div className="grid gap-4 text-xs md:grid-cols-2" data-testid={`user-activity-${u.email}`}>
      <div>
        <p className="mb-2 font-semibold">Aktivne sesije: {active.length} {canRevoke && active.length > 0 && <button onClick={revoke} className="ml-2 text-destructive hover:underline" data-testid={`user-revoke-sessions-${u.email}`}>Prekini sve</button>}</p>
        <ul className="space-y-1">{d.sessions.slice(0, 8).map((s) => <li key={s.id} className={s.active ? "" : "text-muted-foreground line-through"}>{fmtDateTime(s.created_at)} · {s.ip || "—"} · <span className="break-all">{(s.user_agent || "").slice(0, 60)}</span></li>)}</ul>
      </div>
      <div>
        <p className="mb-2 font-semibold">Prijave i odjave</p>
        <ul className="space-y-1">{d.logins.slice(0, 10).map((l, i) => <li key={i}>{fmtDateTime(l.created_at)} · {l.action === "auth.logout" ? "Odjava" : l.action === "auth.login_mfa" ? "Prijava (MFA)" : "Prijava"}</li>)}{!d.logins.length && <li>Nema zapisa.</li>}</ul>
      </div>
    </div>
  );
};

const UserRow = ({ u, me, canManage, reload }) => {
  const [open, setOpen] = useState(false);
  const act = async (path, body, msg) => {
    try {
      await api.put(`/admin/users/${u.id}/${path}`, body);
      toast.success(msg);
      reload();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const self = u.id === me.id;
  return (
    <>
    <tr data-testid={`user-row-${u.email}`}>
      <td className="px-4 py-3"><p className="font-medium">{u.name}</p><p className="text-xs text-muted-foreground">{u.email}</p>{canManage && <button onClick={() => setOpen(!open)} className="text-xs text-pulse hover:underline" data-testid={`user-activity-toggle-${u.email}`}>{open ? "Sakrij aktivnost" : "Sesije i prijave"}</button>}</td>
      <td className="px-4 py-3">
        {canManage && !self ? (
          <select value={u.roles[0] || ""} onChange={(e) => act("role", { role_code: e.target.value }, "Uloga promijenjena")} className={`${inputCls} w-48`} data-testid={`user-role-select-${u.email}`}>{ROLE_CODES.map((r) => <option key={r}>{r}</option>)}</select>
        ) : <span className="font-mono text-xs">{u.roles.join(", ")}</span>}
      </td>
      <td className="px-4 py-3 text-xs">{u.status === "active" ? "Aktivan" : "Deaktiviran"}</td>
      <td className="hidden px-4 py-3 text-xs text-muted-foreground md:table-cell">{fmtDateTime(u.last_login_at)}</td>
      <td className="px-4 py-3">{canManage && !self && (
        <button onClick={() => act("status", { status: u.status === "active" ? "disabled" : "active" }, "Status promijenjen")} className="text-xs font-semibold text-pulse hover:underline" data-testid={`user-toggle-status-${u.email}`}>{u.status === "active" ? "Deaktiviraj" : "Aktiviraj"}</button>
      )}</td>
    </tr>
    {open && <tr><td colSpan={5} className="bg-muted/30 px-4 py-3"><Activity u={u} canRevoke={!self} /></td></tr>}
    </>
  );
};

export default function Users() {
  const { user, can } = useAuth();
  const [tick, setTick] = useState(0);
  const { data, loading } = useFetch("/admin/users", [tick]);
  const roles = useFetch("/admin/roles");
  const reload = () => setTick((t) => t + 1);
  const canManage = can("users.manage");
  const revoke = async (id) => {
    await api.delete(`/admin/invitations/${id}`);
    reload();
  };
  return (
    <div data-testid="admin-users-page">
      <PageTitle title="Korisnici i uloge" sub="Ovlaštenja se provjeravaju na serveru za svaki zahtjev." />
      {canManage && <InviteForm onDone={reload} />}
      {!data ? <BrandLoader /> : (
        <>
          <div className="overflow-x-auto rounded-lg border border-border bg-card">
            <table className="w-full text-sm" data-testid="users-table">
              <thead className="border-b border-border bg-muted/50 text-left text-xs uppercase tracking-wider text-muted-foreground"><tr><th className="px-4 py-3">Korisnik</th><th className="px-4 py-3">Uloga</th><th className="px-4 py-3">Status</th><th className="hidden px-4 py-3 md:table-cell">Zadnja prijava</th><th /></tr></thead>
              <tbody className="divide-y divide-border">{data.users.map((u) => <UserRow key={u.id} u={u} me={user} canManage={canManage} reload={reload} />)}</tbody>
            </table>
          </div>
          {data.invitations.length > 0 && (
            <Panel title="Otvorene pozivnice" className="mt-6" testId="pending-invitations">
              <ul className="divide-y divide-border text-sm">{data.invitations.map((i) => (
                <li key={i.id} className="flex items-center justify-between py-2"><span>{i.email} · <span className="font-mono text-xs">{i.role_code}</span> · ističe {fmtDateTime(i.expires_at)}</span>{canManage && <button onClick={() => revoke(i.id)} className="text-xs text-destructive hover:underline" data-testid={`revoke-invite-${i.email}`}>Opozovi</button>}</li>
              ))}</ul>
            </Panel>
          )}
        </>
      )}
      {roles.data && (
        <Panel title="Matrica ovlaštenja" className="mt-6" testId="permission-matrix">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead><tr><th className="py-2 pr-3 text-left">Ovlaštenje</th>{roles.data.roles.map((r) => <th key={r.code} className="px-2 py-2 font-mono text-[10px]">{r.code}</th>)}</tr></thead>
              <tbody className="divide-y divide-border">{roles.data.permissions.map((p) => (
                <tr key={p.code}><td className="py-1.5 pr-3">{p.label}</td>{roles.data.roles.map((r) => <td key={r.code} className="text-center">{r.permissions.includes(p.code) ? "●" : ""}</td>)}</tr>
              ))}</tbody>
            </table>
          </div>
        </Panel>
      )}
    </div>
  );
}
